/**
 * Client-side rate limiting utilities
 */

const SUBMISSION_COOLDOWN = 5000; // 5 seconds between submissions
const MAX_SUBMISSIONS_PER_HOUR = 10;

/**
 * Check if user can submit form (rate limiting)
 * @param {string} formId - Unique identifier for the form
 * @returns {Object} - { canSubmit: boolean, timeRemaining: number }
 */
export const checkSubmissionRate = (formId) => {
  const now = Date.now();
  const submissions = JSON.parse(localStorage.getItem(`submissions-${formId}`) || '[]');
  
  // Remove submissions older than 1 hour
  const recentSubmissions = submissions.filter(time => now - time < 3600000);
  
  // Check hourly limit
  if (recentSubmissions.length >= MAX_SUBMISSIONS_PER_HOUR) {
    const oldestSubmission = Math.min(...recentSubmissions);
    const timeRemaining = 3600000 - (now - oldestSubmission);
    return { canSubmit: false, timeRemaining };
  }
  
  // Check cooldown period
  const lastSubmission = recentSubmissions[recentSubmissions.length - 1];
  if (lastSubmission && now - lastSubmission < SUBMISSION_COOLDOWN) {
    const timeRemaining = SUBMISSION_COOLDOWN - (now - lastSubmission);
    return { canSubmit: false, timeRemaining };
  }
  
  return { canSubmit: true, timeRemaining: 0 };
};

/**
 * Record a form submission for rate limiting
 * @param {string} formId - Unique identifier for the form
 */
export const recordSubmission = (formId) => {
  const now = Date.now();
  const submissions = JSON.parse(localStorage.getItem(`submissions-${formId}`) || '[]');
  
  // Add current submission
  submissions.push(now);
  
  // Keep only submissions from last hour
  const recentSubmissions = submissions.filter(time => now - time < 3600000);
  
  localStorage.setItem(`submissions-${formId}`, JSON.stringify(recentSubmissions));
};

/**
 * Format time remaining in human-readable format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} - Formatted time string
 */
export const formatTimeRemaining = (milliseconds) => {
  const totalSeconds = Math.ceil(milliseconds / 1000);
  
  if (totalSeconds <= 0) {
    return "0s";
  }
  
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (minutes < 60) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

/**
 * Create a rate-limited submission function
 * @param {Function} submitFunction - Original submit function
 * @param {string} formId - Unique identifier for the form
 * @returns {Function} - Rate-limited submit function
 */
export const createRateLimitedSubmit = (submitFunction, formId) => {
  return async (...args) => {
    const { canSubmit, timeRemaining } = checkSubmissionRate(formId);
    
    if (!canSubmit) {
      const timeString = formatTimeRemaining(timeRemaining);
      throw new Error(`Please wait ${timeString} before submitting again.`);
    }
    
    try {
      const result = await submitFunction(...args);
      recordSubmission(formId);
      return result;
    } catch (error) {
      // Don't record submission on error
      throw error;
    }
  };
}; 